import React, { FC } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Typography,
  useTheme,
  Autocomplete } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextField from 'components/text-field';
import { ContainedButton } from 'components/ContainedButton';
import { ChargeFormEntry, InvolveParty } from '../../types';
import { AmountField } from '../ChargesTableRow/components/Fields/AmountField';
import { ChargesFormType } from '../../hooks';
import { useGeneralChargesContext } from 'contexts/GeneralChargesContext';
import { useMobileCardBaseStyles } from './styles';
import { autocompleteRootProps } from 'v2-utils/autocomplete';

const formatPercent = (percent: number) => {
  const raw = (percent ?? 0) * 100;
  const rounded = Math.round(raw);
  const hasDecimals = Math.abs(raw - rounded) > 1e-6;
  return `${hasDecimals ? '~' : ''}${rounded}%`;
};

const buildPartiesSummary = (parties: InvolveParty[]) =>
  [...parties]
    .filter(({ percent }) => percent > 0)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ name, percent }) => `${name} (${formatPercent(percent)})`)
    .join(' - ');

const useStyles = makeStyles((theme: any) => ({
  numberBadge: {
    minWidth: 36,
    height: 36,
    borderRadius: 6,
    backgroundColor: theme.palette.secondary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.dark,
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: `0 ${theme.spacing(0.5)}`,
    flexShrink: 0,
  },
  descriptionText: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 500,
    color: theme.palette.primary.dark,
    flex: 1,
    lineHeight: 1.4,
    paddingTop: 6,
  },
  endorsementCode: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  sectionLabel: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 500,
    color: theme.palette.tab.offselected,
  },
  headerCompact: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  descriptionRow: {
    width: '100%',
    '& .MuiAutocomplete-root': {
      width: '100%',
    },
    '& .MuiTextField-root': {
      width: '100%',
    },
    '& .MuiInputBase-root': {
      width: '100%',
      wordBreak: 'break-word',
    },
    '& .MuiInputBase-input': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  amountRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(1),
    '& > .MuiFormControl-root': {
      flex: '0 0 50%',
      minWidth: 0,
    },
  },
  partySection: {
    marginTop: theme.spacing(1),
  },
  partyLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  partyNames: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: theme.palette.primary.dark,
    wordBreak: 'break-word',
  },
  noParty: {
    fontSize: '0.8rem',
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  partyAction: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(0.5),
  },
}));

interface Props {
  charge: ChargeFormEntry;
  index: number;
  textPredectiveCharges: any[];
  hideTax: boolean;
  onDeleteCharge: (rowIndex: number) => void;
  onEditSplitAmount: (charge: ChargeFormEntry, kind: 'Payee' | 'Payer' | 'Tax') => void;
  onChargeAmountChange: (newAmount: number | null) => void;
}

export const MobileChargesCardRow: FC<Props> = ({
  charge,
  index,
  textPredectiveCharges,
  hideTax,
  onDeleteCharge,
  onEditSplitAmount,
  onChargeAmountChange,
}) => {
  const localClasses = useStyles();
  const baseClasses = useMobileCardBaseStyles();
  const classes = { ...localClasses, ...baseClasses };
  const { t } = useTranslation();
  const theme = useTheme();
  const { hideEndorsementCode } = useGeneralChargesContext();
  const { control, getValues } = useFormContext<ChargesFormType>();

  const payeesSummary = buildPartiesSummary(charge.payees);
  const payersSummary = buildPartiesSummary(charge.payers);
  const taxesSummary = buildPartiesSummary(charge.taxes);

  const showOverrideIndicator =
    charge.kind !== 'manual' &&
    charge.amount_calculated !== 0 &&
    charge.amount_override !== 0 &&
    charge.amount_calculated !== charge.amount_override;

  const endorsementCode = charge.code.match(/^(owners|lenders)_(\d+)$/)
    ? charge.code.split('_')[1]
    : null;

  const numberLabel = `${hideEndorsementCode || charge.letter === '' ? '' : `${charge.letter}-`}${charge.number}`;

  const filterDescription = (options: string[], value: string) => {
    return options.filter((option) => option.toLowerCase().includes(value.toLowerCase()));
  };

  const description = getValues(`charges.${index}.description`) ?? '';

  return (
    <Paper className={classes.card} variant="outlined">
      <Box className={classes.headerCompact}>
        <Box className={classes.badgeRow}>
          <Box className={classes.numberBadge}>{numberLabel}</Box>
          <Typography className={classes.sectionLabel}>
            {t('charges:chargeDetails')}
          </Typography>

          {charge.kind === 'manual' && (
            <IconButton
              aria-label={t('charges:deleteChargeAriaLabel')}
              className={classes.iconButton}
              style={{ marginLeft: 'auto' }}
              onClick={() => onDeleteCharge(index)}
              size="small"
            >
              <FontAwesomeIcon icon="trash-alt" size="sm" />
            </IconButton>
          )}
        </Box>

        <Box className={classes.descriptionRow}>
          {charge.kind === 'manual' ? (
            <Controller
              name={`charges.${index}.description`}
              control={control}
              render={({ field: { onChange, ref, ...field } }) => (
                <Autocomplete
                  {...field}
                  ref={ref}
                  freeSolo
                  size="small"
                  value={description}
                  options={textPredectiveCharges.map((text: any) => text.description)}
                  filterOptions={(options: string[]) => {
                    const value = getValues(`charges.${index}.description`);
                    if (value) {
                      return filterDescription(options, value);
                    }
                    return options;
                  }}
                  getOptionLabel={(option: any) => option}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                      onChange(value);
                    }
                  }}
                  onChange={(_, value) => {
                    onChange(value);
                  }}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      inputRef={params.InputProps.ref}
                      InputProps={autocompleteRootProps(params.inputProps)}
                      color="secondary"
                      variant="outlined"
                      placeholder={t('charges:description')}
                    />
                  )}
                />
              )}
            />
          ) : (
            <Typography className={classes.descriptionText}>{charge.description || '—'}</Typography>
          )}
        </Box>

        <Box className={classes.amountRow}>
          <Typography className={classes.amountLabel}>{t('charges:totalAmount')}</Typography>
          <AmountField
            index={index}
            name="amount"
            disabled={charge.disabled}
            onAmountChange={onChargeAmountChange}
            borderColor={showOverrideIndicator ? theme.palette.warning.main : undefined}
          />
        </Box>
      </Box>

      <Box className={classes.partySection}>
        <Typography className={classes.partyLabel}>{t('orders:payeesLabel')}</Typography>

        {payeesSummary ? (
          <Typography
            className={classes.partyNames}
            style={{ cursor: charge.disabled ? 'default' : 'pointer' }}
            onClick={() => !charge.disabled && onEditSplitAmount(charge, 'Payee')}
          >
            {payeesSummary}
          </Typography>
        ) : (
          <Typography className={classes.noParty}>
            {t('charges:noPayees')}
          </Typography>
        )}

        {!charge.disabled && (
          <Box className={classes.partyAction}>
            <ContainedButton
              text={t('charges:changePayees')}
              size="small"
              onClick={() => onEditSplitAmount(charge, 'Payee')}
            />
          </Box>
        )}
      </Box>

      <Box className={classes.partySection}>
        <Typography className={classes.partyLabel}>{t('orders:payersLabel')}</Typography>

        {payersSummary ? (
          <Typography
            className={classes.partyNames}
            style={{ cursor: charge.disabled ? 'default' : 'pointer' }}
            onClick={() => !charge.disabled && onEditSplitAmount(charge, 'Payer')}
          >
            {payersSummary}
          </Typography>
        ) : (
          <Typography className={classes.noParty}>
            {t('charges:noPayers')}
          </Typography>
        )}

        {!charge.disabled && (
          <Box className={classes.partyAction}>
            <ContainedButton
              text={t('charges:changePayers')}
              size="small"
              onClick={() => onEditSplitAmount(charge, 'Payer')}
            />
          </Box>
        )}
      </Box>

      {!hideTax && (
        <Box className={classes.partySection}>
          <Typography className={classes.partyLabel}>{t('orders:taxesLabel')}</Typography>

          {taxesSummary ? (
            <Typography
              className={classes.partyNames}
              style={{ cursor: charge.disabled ? 'default' : 'pointer' }}
              onClick={() => !charge.disabled && onEditSplitAmount(charge, 'Tax')}
            >
              {taxesSummary}
            </Typography>
          ) : (
            <Typography className={classes.noParty}>
              {t('charges:noTaxes')}
            </Typography>
          )}

          {!charge.disabled && (
            <Box className={classes.partyAction}>
              <ContainedButton
                text={t('charges:changeTaxes')}
                size="small"
                onClick={() => onEditSplitAmount(charge, 'Tax')}
              />
            </Box>
          )}
        </Box>
      )}

      {!hideEndorsementCode && endorsementCode && (
        <Typography className={classes.endorsementCode}>
          {t('charges:endorsementCode', { code: endorsementCode })}
        </Typography>
      )}
    </Paper>
  );
};
